/**
 * Engine player environment detection.
 *
 * When running inside the .NET V8 host (GameRuntime), several globals are
 * injected that don't exist in a regular browser:
 *   - `isEnginePlayer`  — () => true
 *   - `canvas`          — pre-created CanvasElement
 *   - `resource`        — ResourceManager with file-system backed loaders
 *
 * Standard Web APIs like `fetch`, `document.getElementById`, etc. are **not**
 * available in the engine player.
 */

// Ambient globals injected by the .NET V8 host
declare const isEnginePlayer: (() => boolean) | undefined;
declare const canvas: HTMLCanvasElement | undefined;

/** True when running inside the native .NET V8 engine player */
export const IS_ENGINE_PLAYER: boolean = typeof isEnginePlayer === 'function';

/** Host-created canvas (engine player only, undefined in browser) */
export const hostCanvas: HTMLCanvasElement | undefined =
  IS_ENGINE_PLAYER ? canvas : undefined;

/**
 * ResourceManager interface exposed by the .NET host as the `resource` global.
 * Methods return Promises (ClearScript converts Task<T> → Promise<T>).
 */
export interface HostResourceManager {
  manifest: unknown;
  getTextResourceAsync(idOrPath: string): Promise<string>;
  getBinaryResourceAsync(idOrPath: string): Promise<ArrayBuffer>;
  getImageResourceAsync(idOrPath: string): Promise<HTMLImageElement>;
  exists(idOrPath: string): boolean;
  resolvePath(idOrPath: string): string;
  getAssetCategory(id: string): string | null;
  preloadAsync(ids: string[]): Promise<void>;
  clearCache(): void;
}

declare const resource: HostResourceManager | undefined;

/** The host ResourceManager (engine player only, undefined in browser) */
export const hostResource: HostResourceManager | undefined =
  IS_ENGINE_PLAYER ? resource : undefined;

/**
 * Native audio bridge exposed by the .NET host as the `audioHost` global (miniaudio).
 * Absent when no playback device opened — callers fall back to the Web Audio engine.
 */
export interface HostAudioBridge {
  readonly isAvailable: boolean;
  registerClip(clipId: string, data: ArrayBuffer): void;
  isClipRegistered(clipId: string): boolean;
  play(clipId: string, opts: {
    volume?: number; pitch?: number; loop?: boolean; fadeIn?: number; spatial?: boolean;
    x?: number; y?: number; z?: number;
    attenuation?: number; minDistance?: number; maxDistance?: number;
    rolloff?: number; doppler?: number;
  }): number;
  stop(voiceId: number): void;
  setVoiceVolume(voiceId: number, volume: number): void;
  setVoicePitch(voiceId: number, pitch: number): void;
  setVoicePosition(voiceId: number, x: number, y: number, z: number): void;
  setVoiceVelocity(voiceId: number, x: number, y: number, z: number): void;
  isVoicePlaying(voiceId: number): boolean;
  setListener(
    x: number, y: number, z: number,
    fx: number, fy: number, fz: number,
    ux: number, uy: number, uz: number,
    vx: number, vy: number, vz: number,
  ): void;
  setMasterVolume(volume: number): void;
  stopAll(): void;
}

declare const audioHost: HostAudioBridge | undefined;

/** The host audio bridge (engine player only; undefined in browser or if no device). */
export const hostAudio: HostAudioBridge | undefined =
  IS_ENGINE_PLAYER && typeof audioHost !== 'undefined' ? audioHost : undefined;
